import { afterNextRender, ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Input, OnDestroy, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { BasketReturnModel, Money, OrderReturnModel } from 'src/business/services/client.generated';
import { BasketService } from '../../services/basket.service';

@Component({
    selector: 'app-basket-summary',
    templateUrl: './basket-summary.component.html',
    styleUrls: ['./basket-summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketSummaryComponent implements OnDestroy {
    prices: Signal<Array<{ text: string, price: Money, data?: { [key: string]: string } }>>;
    subscription: Subscription;
    basketInput = signal<BasketReturnModel | null>(null)
    onFormChange: (ev: Event) => void;

    @Input('basket')
    set _basket(b: BasketReturnModel) {
        this.basketInput.set(b);
    }

    constructor(private basketService: BasketService, private _detector: ChangeDetectorRef) {
        let basketSignal = toSignal(this.basketService.basket$);
        let paymentFeeSignal = signal<{ price: number, vat: number, name: string, data?: { [key: string]: string } }>({ price: 0, vat: 0, name: '' });
        this.onFormChange = (ev: Event) => {
            let elm = (ev.target as HTMLInputElement);
            if (elm.name == 'paymentMethodId')
                paymentFeeSignal.set({ price: +(elm.getAttribute('price') ?? ''), vat: +(elm.getAttribute('vat') ?? ''), name: elm.getAttribute('label') })
        }
        this.prices = computed(() => {
            let basketFromInput = this.basketInput();
            let basketFromService = basketSignal();
            let basket = basketFromInput ?? basketFromService;
            let paymentFee = paymentFeeSignal();
            if (basket == null)
                return [];
            let prices: Array<{ text: string, price: Money }> = [];
            prices.push(this._totalExVat(basket))
            prices.push(this._totalShipping(basket))
            prices.push(this._ecological(basket))
            prices.push(this._paymentTermFee(basket as OrderReturnModel, paymentFee))
            prices.push(this._totalVat(basket, paymentFee.vat))
            prices.push(this._totalDiscount(basket))
            prices.push({ text: 'line', price: { amount: 0, currency: 'XXX' } });
            prices.push(this._total(basket, paymentFee.price + paymentFee.vat))
            return prices.filter(p => p != null);
        });
        afterNextRender(() => {
            document?.forms?.namedItem('payment-form')?.addEventListener('change', this.onFormChange, { passive: true })
        })
    }
    ngOnDestroy(): void {
        document?.forms?.namedItem('payment-form')?.removeEventListener('change', this.onFormChange)
    }
    private _totalShipping(basket: BasketReturnModel) {
        if ((basket?.shippingTotal?.amount ?? 0) > 0)
            return { text: 'total_shipping', price: { amount: basket.shippingTotal.amount - (basket?.shippingTotalVat?.amount ?? 0), currency: basket.shippingTotal.currency } };
        return;
    }
    private _total(basket: BasketReturnModel, paymentFee: number) {
        if ((basket?.total?.amount ?? 0) > 0)
            return {
                text: 'total_in_vat', price: {
                    amount: basket?.total?.amount + paymentFee,
                    currency: basket?.total?.currency
                }
            };
        return;
    }
    private _totalDiscount(basket: BasketReturnModel) {
        if ((basket?.totalDiscount?.amount ?? 0) > 0)
            return { text: 'total_discount', price: basket.totalDiscount };
        return;
    }
    private _paymentTermFee(basket: BasketReturnModel | OrderReturnModel, fee: { price: number, vat: number, name: string }) {
        if (fee?.price ?? 0 > 0) // TODO
            return { text: 'total_fee', price: { amount: fee?.price, currency: basket?.currency }, data: { name: fee.name } };

        if (Array.isArray(basket?.paymentTerms))
            return;

        let payment = (basket as OrderReturnModel).paymentTerms;
        if ((payment?.amount?.amount ?? 0) > 0) {
            return { text: 'total_fee', price: { amount: payment.amount.amount - (payment?.vat?.amount ?? 0), currency: payment.amount.currency }, data: { name: payment.name } };
        }

        return;
    }
    private _ecological(basket: BasketReturnModel) {
        if ((basket.ecoFee?.amount ?? 0) > 0)
            return { text: 'total_ecoFee', price: { amount: basket.ecoFee.amount - (basket?.ecoFeeVat?.amount ?? 0), currency: basket.ecoFee.currency } }
        return;
    }
    private _totalVat(basket: BasketReturnModel, paymentFee: number) {
        if ((basket?.totalVat?.amount ?? 0) > 0)
            return {
                text: 'total_vat', price: {
                    amount: basket?.totalVat?.amount + paymentFee,
                    currency: basket?.totalVat?.currency
                }
            };
        return;
    }
    private _totalExVat(basket: BasketReturnModel) {
        if ((basket?.subtotal?.amount ?? 0) > 0)
            return {
                text: 'total_ex_vat', price: {
                    amount:
                        (basket?.subtotal?.amount ?? 0) - (basket?.subtotalVat?.amount ?? 0)
                    , currency: (basket?.subtotal?.currency ?? 'XXX')
                }
            };
        return;
    }

}
